import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { BottomNav } from "@/components/BottomNav";
import { MbCard } from "@/components/MbCard";
import { useLanguage } from "@/contexts/LanguageContext";
import { Play, Video } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { VideoPlayerDialog } from "@/components/VideoPlayerDialog";
import { AppFooter } from "@/components/AppFooter";

interface PromoVideo {
  id: string;
  title_gu: string;
  title_hi: string | null;
  description_gu: string | null;
  description_hi: string | null;
  yt_video_id: string;
  video_language: 'gu' | 'hi';
  display_order: number;
}

export default function PromoVideos() {
  const [videos, setVideos] = useState<PromoVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<{ id: string; title: string } | null>(null);
  const navigate = useNavigate();
  const { logo, strings, language } = useLanguage();

  const getVideoContent = (video: PromoVideo) => {
    if (language === 'hi') {
      return {
        title: video.title_hi || video.title_gu,
        description: video.description_hi || video.description_gu,
      };
    }
    return {
      title: video.title_gu,
      description: video.description_gu,
    };
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setAuthLoading(false);
        if (!session) {
          navigate("/auth");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (!session) {
        navigate("/auth");
      } else {
        fetchPromoVideos();
      }
    });

    return () => subscription.unsubscribe();
  }, [language]);

  const fetchPromoVideos = async () => {
    try {
      const { data, error } = await supabase
        .from("promo_videos")
        .select("*")
        .eq("is_active", true)
        .eq("video_language", language)
        .order("display_order", { ascending: true });

      if (error) throw error;

      const videoData = (data || []).map((video) => ({
        ...video,
        video_language: (video.video_language as 'gu' | 'hi'),
      }));

      setVideos(videoData);
    } catch (error: any) {
      console.error("Error fetching promo videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (videoId: string, title: string) => {
    setSelectedVideo({ id: videoId, title });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-20">
        <div className="animate-pulse text-muted-foreground">{strings.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))] pb-20">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="દમયંતી બાગ" className="h-10" />
            <h1 className="text-xl font-bold text-primary">{strings.promoVideos}</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-4">
        {videos.length > 0 ? (
          videos.map((video) => {
            const content = getVideoContent(video);
            const thumbnailUrl = `https://img.youtube.com/vi/${video.yt_video_id}/maxresdefault.jpg`;

            return (
              <MbCard key={video.id} elevated className="overflow-hidden">
                <div className="relative">
                  {/* Thumbnail */}
                  <div className="relative aspect-video">
                    <img
                      src={thumbnailUrl}
                      alt={content.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = `https://img.youtube.com/vi/${video.yt_video_id}/hqdefault.jpg`;
                      }}
                    />
                    
                    {/* Play Button Overlay */}
                    <button
                      onClick={() => handlePlay(video.yt_video_id, content.title)}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors group"
                    >
                      <div className="w-20 h-20 rounded-full bg-primary/90 group-hover:bg-primary flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                        <Play className="w-10 h-10 text-primary-foreground ml-1" fill="currentColor" />
                      </div>
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-bold text-lg text-foreground mb-2 line-clamp-2">
                      {content.title}
                    </h3>
                    
                    {content.description && (
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {content.description}
                      </p>
                    )}
                  </div>
                </div>
              </MbCard>
            );
          })
        ) : (
          <MbCard className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Video className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-bold mb-2">{strings.noPromoVideos}</h2>
            <p className="text-muted-foreground">{strings.newPromoVideosWillAppear}</p>
          </MbCard>
        )}
      </div>

      <div className="pb-16">
        <AppFooter />
      </div>

      {selectedVideo && (
        <VideoPlayerDialog
          videoId={selectedVideo.id}
          isOpen={!!selectedVideo}
          sessionTitle={selectedVideo.title}
          onClose={() => setSelectedVideo(null)}
        />
      )}

      <BottomNav />
    </div>
  );
}
